import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Toolbar,
  Divider,
  Link,
  Select,
  MenuItem,
} from '@mui/material';

import {
  DataGrid,
  GridToolbar,
  type GridColDef,
  type GridFilterModel,
  type GridPaginationModel,
} from '@mui/x-data-grid';
import { Link as RouterLink } from 'react-router';
import type { GridRenderCellParams, GridValueGetter } from '@mui/x-data-grid';

import { useUser } from '@/providers/UserContext';
import LoadingMask from '@/components/shared/LoadingMask';

import { getTasks } from '@/actions/task';
import { useApi } from '@/providers/ApiContext';
import type { Task } from '@/types';

const PAGE_SIZE = 50;

function AccQsureAdminTasks(): React.JSX.Element {
  const user = useUser();
  const api = useApi();
  const [tasks, setTasks] = useState<Task[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [cursors, setCursors] = useState<Record<number, string | null>>({});
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [debouncedSearchFilter, setDebouncedSearchFilter] =
    useState<string>('');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    {},
  );
  const [debouncedColumnFilters, setDebouncedColumnFilters] = useState<
    Record<string, string>
  >({});
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    quickFilterValues: [],
  });
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const columnFilterTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounce search filter changes - only trigger fetch, don't reset state
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchFilter(searchFilter);
    }, 500); // Longer delay to prevent excessive API calls

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchFilter]);

  // Debounce column filter changes - only trigger fetch, don't reset state
  useEffect(() => {
    if (columnFilterTimeoutRef.current) {
      clearTimeout(columnFilterTimeoutRef.current);
    }

    columnFilterTimeoutRef.current = setTimeout(() => {
      setDebouncedColumnFilters(columnFilters);
    }, 200); // Shorter delay for dropdown selections

    return () => {
      if (columnFilterTimeoutRef.current) {
        clearTimeout(columnFilterTimeoutRef.current);
      }
    };
  }, [columnFilters]);

  const fetchTasks = useCallback(
    async (page: number, startKey: string | null = null): Promise<void> => {
      setLoading(true);
      try {
        // Extract status filter from debounced column filters
        const statusFilter = debouncedColumnFilters['status'] || undefined;

        console.log('Fetching tasks with filters:', {
          statusFilter,
          searchFilter: debouncedSearchFilter,
          columnFilters: debouncedColumnFilters,
        });

        if (!api) return;
        const response = await getTasks(
          api,
          PAGE_SIZE,
          startKey || undefined,
          statusFilter,
          debouncedSearchFilter || undefined,
        );
        setTasks(response.results);
        setHasNextPage(response.last_key !== null);
        if (response.last_key) {
          setCursors((prev) => ({
            ...prev,
            [page + 1]: response.last_key,
          }));
        }
      } catch (error) {
        console.log(error);
        setTasks([]);
      } finally {
        setLoading(false);
      }
    },
    [api, debouncedSearchFilter, debouncedColumnFilters],
  );

  useEffect(() => {
    if (user?.entity_id && !tasks) {
      setCurrentPage(0);
      setCursors({});
      fetchTasks(0);
    }
  }, [user?.entity_id, tasks, fetchTasks]);

  // Reset pagination when debounced search filter or column filters change
  useEffect(() => {
    if (user?.entity_id) {
      setCurrentPage(0);
      setCursors({});
      // Don't reset tasks to null - just fetch new data
      fetchTasks(0);
    }
  }, [
    debouncedSearchFilter,
    debouncedColumnFilters,
    user?.entity_id,
    fetchTasks,
  ]);

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel): void => {
      console.log('Filter model changed:', model);

      // Update the filter model state to maintain it
      setFilterModel(model);

      // Handle QuickFilter - only update if it's actually different
      const newSearchFilter = model.quickFilterValues?.join(' ') || '';
      if (newSearchFilter !== searchFilter) {
        console.log(
          'Search filter changed from:',
          searchFilter,
          'to:',
          newSearchFilter,
        );
        setSearchFilter(newSearchFilter);
      }

      // Handle column filters
      const newColumnFilters: Record<string, string> = {};
      if (model.items) {
        model.items.forEach((item) => {
          if (
            item.field &&
            item.value !== undefined &&
            item.value !== null &&
            item.value !== ''
          ) {
            newColumnFilters[item.field] = String(item.value);
          }
        });
      }

      console.log('New column filters:', newColumnFilters);

      // Only update if column filters have changed
      const columnFiltersChanged =
        JSON.stringify(newColumnFilters) !== JSON.stringify(columnFilters);
      if (columnFiltersChanged) {
        setColumnFilters(newColumnFilters);
      }
    },
    [searchFilter, columnFilters],
  );

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel): void => {
      if (model.page > currentPage && hasNextPage) {
        setCurrentPage(model.page);
        const nextCursor = cursors[model.page];
        if (nextCursor) {
          fetchTasks(model.page, nextCursor);
        }
      } else if (model.page < currentPage) {
        setCurrentPage(model.page);
        if (model.page === 0) {
          fetchTasks(0);
        } else {
          const prevCursor = cursors[model.page];
          if (prevCursor) {
            fetchTasks(model.page, prevCursor);
          }
        }
      }
    },
    [currentPage, hasNextPage, cursors, fetchTasks],
  );

  const renderName = useCallback(
    (params: GridRenderCellParams<Task>): React.JSX.Element => {
      return (
        <Link component={RouterLink} to={`/admin/accqsure/tasks/${params.id}`}>
          {params.row.name}
        </Link>
      );
    },
    [],
  );

  const convertDate = useCallback((value: string): Date | null => {
    if (!value) return null;
    return new Date(value.replace(' ', 'T') + 'Z');
  }, []);

  const columns: GridColDef<Task>[] = useMemo(
    () => [
      {
        field: 'entity_id',
        headerName: 'Id',
        flex: 0.5,
        type: 'string',
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        type: 'string',
        renderCell: renderName,
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'singleSelect',
        flex: 0.5,
        filterable: true,
        filterOperators: [
          {
            label: 'Equals',
            value: 'equals',
            getApplyFilterFn: (filterItem) => {
              if (!filterItem.field || !filterItem.value) {
                return null;
              }
              return (params) => {
                return params.value === filterItem.value;
              };
            },
            InputComponent: ({ item, applyValue }) => (
              <Box
                sx={{
                  flexDirection: 'row',
                }}
              >
                <Typography
                  variant='caption'
                  sx={{ display: 'block', color: 'text.secondary' }}
                >
                  Value
                </Typography>
                <Select
                  value={item.value || ''}
                  onChange={(event) =>
                    applyValue({ ...item, value: event.target.value })
                  }
                  displayEmpty
                  size='small'
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value=''>
                    <em>All</em>
                  </MenuItem>
                  <MenuItem value='queued'>Queued</MenuItem>
                  <MenuItem value='running'>Running</MenuItem>
                  <MenuItem value='canceled'>Canceled</MenuItem>
                  <MenuItem value='finished'>Finished</MenuItem>
                  <MenuItem value='failed'>Failed</MenuItem>
                </Select>
              </Box>
            ),
          },
        ],
      },
      {
        field: 'created_at',
        headerName: 'Started',
        type: 'dateTime',
        flex: 0.75,
        valueGetter: ((_value, row) => {
          if (!row.created_at) return null;
          return convertDate(row.created_at);
        }) as GridValueGetter<Task>,
      },
      {
        field: 'updated_at',
        headerName: 'Last Updated',
        type: 'dateTime',
        flex: 0.75,
        valueGetter: ((_value, row) => {
          if (!row.updated_at) return null;
          return convertDate(row.updated_at);
        }) as GridValueGetter<Task>,
      },
    ],
    [renderName, convertDate],
  );

  if (!tasks) {
    return <LoadingMask />;
  }

  return (
    <>
      <Toolbar
        sx={{
          minHeight: '55px !important',
        }}
      >
        <Typography variant='h6' sx={{ mr: 4 }}>
          Task Engine Tasks
        </Typography>
      </Toolbar>
      <Divider />

      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box
            sx={{
              height: 'calc(100vh - 280px)',
              width: '100%',
              flexGrow: 1,
            }}
          >
            <DataGrid
              columnHeaderHeight={35}
              sx={(theme) => ({
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.grey[100],
                },
              })}
              getRowId={(row) => row.entity_id}
              columns={columns}
              rows={tasks || []}
              showToolbar
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  sx: {
                    button: {
                      color: 'black',
                    },
                    svg: {
                      color: 'black',
                    },
                  },
                  showQuickFilter: true,
                  quickFilterProps: {
                    debounceMs: 500,
                    placeholder: 'Search tasks...',
                  },
                },
              }}
              paginationMode='server'
              filterMode='server'
              filterModel={filterModel}
              rowCount={
                hasNextPage ? -1 : currentPage * PAGE_SIZE + tasks.length
              }
              pageSizeOptions={[PAGE_SIZE]}
              paginationModel={{
                pageSize: PAGE_SIZE,
                page: currentPage,
              }}
              onPaginationModelChange={handlePaginationModelChange}
              onFilterModelChange={handleFilterModelChange}
              loading={loading}
              disableColumnSelector
              disableDensitySelector
            />
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default AccQsureAdminTasks;
